// src/components/Header.tsx
import './Header.css';
import { NavigationMenu, NavigationMenuItem } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useUser } from '@/hooks/user-user';
import { Separator } from '@/components/ui/separator';
import allRoutes, { RoutePath } from '@/lib/routes';
import AccountSettings from './AccountSettings';
import { motion } from 'framer-motion';
import AnimatedNavbar from './AnimatedNav';
import { useNavigate } from 'react-router-dom';

export type LinkConfig = {
  label: string;
  path: RoutePath;
  disabled: boolean;
  position: (screenSize: ScreenSize) => { top: string; left: string };
  paths: {
    length: number;
    direction: 'horizontal' | 'vertical' | 'diagonal';
  }[];
};

type ScreenSize = 'small' | 'medium' | 'large';

type Props = {
  children?: React.ReactNode;
};

export const Header = ({ children }: Props) => {
  const { username, teamName, isGlobalAdmin, isGroupAdmin, accessibleProtectedRoutes, userIsSet } = useUser();
  const navigate = useNavigate();

  const linkList: LinkConfig[] = [
    {
      label: 'Scouting',
      path: allRoutes.chatbot.route,
      disabled: !accessibleProtectedRoutes.includes(allRoutes.chatbot.route),
      position: (screenSize) => {
        return {
          top: screenSize === 'small' ? '35svh' : screenSize === 'medium' ? '47svh' : '43.9svh',
          left: screenSize === 'small' ? '0svw' : screenSize === 'medium' ? '5svw' : '23svw',
        };
      },
      paths: [
        {
          length: 40,
          direction: 'diagonal',
        },
        {
          direction: 'horizontal',
          length: 45,
        },
        {
          direction: 'diagonal',
          length: 10,
        },
      ],
    },

    // TODO: uncomment when the page is ready or remove if not needed
    // {
    //   label: "Dashboard",
    //   path: "/dashboard",
    // },

    //TODO: temp disabled Club Controlling
    // {
    //   label: 'Club Controlling',
    //   path: allRoutes.qbusiness.route,
    //   disabled: !accessibleProtectedRoutes.includes(allRoutes.qbusiness.route),
    //   position: (screenSize) => {
    //     return {
    //       top: screenSize === 'small' ? '34.8svh' : screenSize === 'medium' ? '34svh' : '27svh',
    //       left: screenSize === 'small' ? '58svw' : screenSize === 'medium' ? '65svw' : '65svw',
    //     };
    //   },
    //   paths: [
    //     {
    //       length: 50,
    //       direction: 'horizontal',
    //     },
    //     {
    //       length: 55,
    //       direction: 'vertical',
    //     },
    //     {
    //       length: 15,
    //       direction: 'diagonal',
    //     },
    //     {
    //       length: 20,
    //       direction: 'horizontal',
    //     },
    //   ],
    // },

    {
      label: 'Verein- und Benutzermanager',
      path: allRoutes.groupManager.route,
      disabled: !isGroupAdmin && !isGlobalAdmin,
      position: (screenSize) => {
        return {
          top: screenSize === 'small' ? '69svh' : screenSize === 'medium' ? '69svh' : '78svh',
          left: screenSize === 'small' ? '43svw' : screenSize === 'medium' ? '15svw' : '25svw',
        };
      },
      paths: [
        {
          length: 90,
          direction: 'vertical',
        },
      ],
    },
  ];

  const initialOpen = !linkList
    .map((link) => link.path.replace(/\//g, '') as string)
    .includes(location.pathname.replace(/\//g, ''));
  const [expanded, setExpanded] = useState(initialOpen);

  const UserInfoDisplay = () => {
    return (
      <div className="text-sm flex overflow-hidden min-w-0">
        <p title={username} className="truncate whitespace-nowrap min-w-0">
          {username}
        </p>
        <div className="mx-1">
          <Separator orientation="vertical" className="bg-black dark:bg-white opacity-50" />
        </div>
        <p className="capitalize whitespace-nowrap flex space-x-4">{teamName}</p>
      </div>
    );
  };

  if (!userIsSet) {
    navigate('/');
    return null;
  }
  return (
    <>
      <motion.div
        className="w-full "
        initial={{ height: expanded ? '100svh' : 'fit-content' }}
        animate={expanded ? { height: '100svh' } : { height: 'fit-content' }}
        transition={{ duration: 0.15 }}>
        <NavigationMenu
          className={cn(
            'shadow-md shadow-accent/17 dark:shadow-neutral-900 px-2 py-1 max-w-[full] h-full z-[20] ',
            expanded ? 'bg-transparent' : 'bg-background'
          )}>
          <NavigationMenuItem className="flex w-full">
            <div className={expanded ? 'h-[100svh]  w-full' : ''}>
              <AnimatedNavbar expanded={expanded} linkList={linkList} setExpanded={setExpanded} />
            </div>

            {!expanded ? (
              <div className="ml-auto flex opacity-70 min-w-0  gap-2 overflow-hidden  items-center ">
                <UserInfoDisplay />

                <div className="p-2 bg-muted/50 flex items-center justify-center rounded-full">
                  <AccountSettings />
                </div>
              </div>
            ) : null}
          </NavigationMenuItem>
        </NavigationMenu>
      </motion.div>
      {!expanded && children ? children : null}
    </>
  );
};

export default Header;
